import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Lock, LogOut, PlayCircle, CalendarDays, ShieldCheck, Film } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "recordings_access";

const parseLocal = (dateStr) => {
  if (!dateStr) return null;
  const clean = dateStr.replace("Z", "").replace(/[+-]\d{2}:\d{2}$/, "");
  const [dp, tp] = clean.split("T");
  if (!dp) return null;
  const [y, m, d] = dp.split("-").map(Number);
  const [h, mi] = (tp || "00:00").split(":").map(Number);
  return new Date(y, m - 1, d, h || 0, mi || 0);
};

const formatRelease = (d) => {
  const dt = parseLocal(d);
  if (!dt || isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" });
};

export default function RecordingsPage() {
  const [identifier, setIdentifier] = useState("");
  const [auth, setAuth] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("ms");
  const [recordings, setRecordings] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  // noindex + title
  useEffect(() => {
    document.title = "Záznamy ukázek | Agent ŠVP";
    let meta = document.querySelector('meta[name="robots"]');
    let created = false;
    let prevContent = "";
    if (meta) {
      prevContent = meta.getAttribute("content") || "";
      meta.setAttribute("content", "noindex, nofollow");
    } else {
      meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex, nofollow";
      document.head.appendChild(meta);
      created = true;
    }
    return () => {
      if (created) document.head.removeChild(meta);
      else meta.setAttribute("content", prevContent);
    };
  }, []);

  // restore session
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        const a = JSON.parse(saved);
        setAuth(a);
        setTab(a.school_type === "zs" ? "zs" : "ms");
      }
    } catch (e) { /* ignore */ }
  }, []);

  // load recordings when authenticated / tab changes
  useEffect(() => {
    if (!auth) return;
    const load = async () => {
      setLoadingRecs(true);
      try {
        const res = await axios.post(`${API}/recordings/list`, { identifier: auth.identifier, school_type: tab });
        if (res.data.recordings) setRecordings(res.data.recordings);
        else logout();
      } catch (e) { setRecordings([]); }
      finally { setLoadingRecs(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, tab]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/recordings/verify`, { identifier: identifier.trim() });
      if (res.data.allowed) {
        const a = { identifier: identifier.trim(), name: res.data.name || "", school_name: res.data.school_name || "", school_type: res.data.school_type || "" };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(a));
        setAuth(a);
        setTab(res.data.school_type === "zs" ? "zs" : "ms");
      } else {
        setError(res.data.message || "Přístup zamítnut.");
      }
    } catch (err) {
      setError("Nepodařilo se ověřit přístup. Zkuste to prosím znovu.");
    } finally { setLoading(false); }
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuth(null);
    setIdentifier("");
    setRecordings([]);
  };

  const isValidUrl = (u) => /^https?:\/\//i.test(u || "");

  // ---------- GATE ----------
  if (!auth) {
    return (
      <div className="min-h-screen bg-[#F4F7FC] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-[#E8EDF5] overflow-hidden">
            <div className="bg-gradient-to-r from-[#0049B3] to-[#003380] px-8 py-8 text-center">
              <div className="w-14 h-14 bg-[#FFC72C] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Film className="w-7 h-7 text-[#0F1B3D]" />
              </div>
              <h1 className="text-xl sm:text-2xl font-extrabold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Záznamy ukázek</h1>
              <p className="text-blue-200/70 text-sm mt-2">Agent ŠVP — sekce pouze pro registrované účastníky</p>
            </div>
            <div className="p-8">
              <p className="text-sm text-[#4A5B7A] mb-5 leading-relaxed">
                Pro přístup k záznamům zadejte <strong>e-mail nebo IČO</strong>, které jste použili při registraci na živou ukázku.
              </p>
              <form onSubmit={handleVerify} className="space-y-4">
                <Input
                  data-testid="recordings-identifier-input"
                  type="text"
                  placeholder="vas@email.cz nebo IČO školy"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="rounded-xl py-6 text-base"
                  autoFocus
                />
                {error && <p data-testid="recordings-error" className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
                <Button data-testid="recordings-unlock-button" type="submit" disabled={loading} className="w-full bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl py-6 font-bold text-base">
                  <Lock className="w-4 h-4 mr-2" />{loading ? "Ověřuji..." : "Odemknout záznamy"}
                </Button>
              </form>
              <div className="flex items-center gap-2 mt-6 text-xs text-[#9CA3AF]">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span>Přístup mají pouze školy registrované na živou ukázku Agenta ŠVP.</span>
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-[#9CA3AF] mt-6">#TouchOfLucky s.r.o. | agentsvp.cz</p>
        </div>
      </div>
    );
  }

  // ---------- CONTENT ----------
  return (
    <div className="min-h-screen bg-[#F4F7FC]">
      <div className="bg-gradient-to-r from-[#0049B3] to-[#003380]">
        <div className="max-w-5xl mx-auto px-4 py-8 sm:py-10 flex items-start justify-between gap-4">
          <div>
            <p className="text-[#FFC72C] text-xs font-bold uppercase tracking-widest mb-2">Agent ŠVP</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Záznamy ukázek</h1>
            <p className="text-blue-200/70 text-sm mt-2" data-testid="recordings-greeting">
              Vítejte{auth.name ? `, ${auth.name}` : ""}{auth.school_name ? ` — ${auth.school_name}` : ""}
            </p>
          </div>
          <Button data-testid="recordings-logout-button" variant="outline" onClick={logout} className="rounded-xl border-white/20 text-white hover:bg-white/10 hover:text-white bg-transparent">
            <LogOut className="w-4 h-4 mr-2" />Odhlásit
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex gap-2 mb-8 flex-wrap">
          {[{ id: "ms", label: "Mateřské školy" }, { id: "zs", label: "Základní školy" }].map(t => (
            <button key={t.id} data-testid={`recordings-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-5 py-3 rounded-xl font-semibold text-sm transition-all ${tab === t.id ? "bg-[#0049B3] text-white shadow-lg" : "bg-white text-[#3D4F6F] border border-[#E8EDF5] hover:bg-[#F8FAFF]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loadingRecs ? (
          <p className="text-center text-[#6B7A99] py-16" data-testid="recordings-loading">Načítám záznamy...</p>
        ) : (
          <div className="space-y-8">
            {recordings.map((r, i) => (
              <div key={r.slot || i} data-testid={`recording-card-${r.slot || i}`} className="bg-white rounded-2xl border border-[#E8EDF5] shadow-sm overflow-hidden">
                <div className="px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0049B3]/10 flex items-center justify-center">
                      <PlayCircle className="w-5 h-5 text-[#0049B3]" />
                    </div>
                    <h2 className="text-base md:text-lg font-bold text-[#0F1B3D]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{r.title || `Záznam ukázky ${i + 1}`}</h2>
                  </div>
                  {r.release_date && (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[#6B7A99] bg-[#F4F7FC] px-3 py-1.5 rounded-full" data-testid={`recording-date-${r.slot || i}`}>
                      <CalendarDays className="w-3.5 h-3.5" />Zveřejněno {formatRelease(r.release_date)}
                    </span>
                  )}
                </div>
                {isValidUrl(r.embed_url) ? (
                  <div className="aspect-video bg-black">
                    <iframe src={r.embed_url} title={r.title || `Záznam ${i + 1}`} className="w-full h-full" frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
                  </div>
                ) : (
                  <div className="aspect-video bg-[#F8FAFF] flex items-center justify-center">
                    <p className="text-sm text-[#9CA3AF]">Záznam zatím nebyl zveřejněn.</p>
                  </div>
                )}
              </div>
            ))}
            {recordings.length === 0 && <p className="text-center text-[#9CA3AF] py-16" data-testid="recordings-empty">Zatím zde nejsou žádné záznamy.</p>}
          </div>
        )}

        <p className="text-center text-xs text-[#9CA3AF] mt-12">#TouchOfLucky s.r.o. | agentsvp.cz</p>
      </div>
    </div>
  );
}
